import { useEffect, useState } from "react";
import type { AvailableSession } from "@teamna/shared";
import { formatClock, formatDuration, formatMeridiem } from "@teamna/web-client/lib/format.ts";
import { Badge, type BadgeTone } from "./Badge.tsx";

/**
 * One session in the owner's week.
 *
 * Deliberately the same object as the member's `SessionCard`: the time rail, the coloured spine,
 * the title, the badge. An owner who is also a member moves between the two tabs all day, and a
 * session that changes shape depending on who is looking at it reads as two different things.
 *
 * What changes is the reading. A member wants to know whether they are booked; an owner wants to
 * know how full the room is, so the badge counts free spots and the second line counts heads.
 *
 * A session the API has marked `moved` is a tombstone: its bookings already travelled to the
 * replacement row, which sits somewhere else in this same list. A green "3 free" badge on it would
 * read as a live session with room in it, so it is muted, labelled, and offers no actions.
 *
 * From the start of a session the first badge says where attendance is (`attendance`): taken with
 * how many came, partly checked, or not taken once it ended (pilot feedback, 2026-10-22).
 */
export function OwnerSessionCard({
  session,
  timezone,
  attendance = null,
  onSelect,
}: {
  session: AvailableSession;
  timezone: string;
  attendance?: { label: string; tone: BadgeTone } | null;
  onSelect?: () => void;
}) {
  // Draws the card again at the end, so the badge and the second line change while the week is open.
  useSessionEnded(session.endsAt);
  const status = ownerSessionBadge(session);
  const muted = session.status !== "scheduled";
  const spine = session.groupColor ?? "var(--color-accent-soft)";
  const duration = formatDuration(session.startsAt, session.endsAt);

  const body = (
    <>
      <div className="flex w-14 shrink-0 flex-col items-end pt-0.5 text-right">
        <span
          className={[
            "text-[20px] leading-none font-semibold tabular-nums",
            muted ? "text-ink-3" : "text-ink",
          ].join(" ")}
        >
          {formatClock(session.startsAt, timezone)}
        </span>
        <span className="mt-1 text-[11px] font-semibold tracking-[0.08em] text-ink-3">
          {formatMeridiem(session.startsAt, timezone)}
        </span>
      </div>

      <span
        className="w-[3px] shrink-0 self-stretch rounded-full"
        style={{ backgroundColor: muted ? "var(--color-line-strong)" : spine }}
        aria-hidden="true"
      />

      <div className="min-w-0 flex-1">
        <p
          className={[
            "truncate text-[15px] font-semibold",
            muted ? "text-ink-2 line-through decoration-line-strong" : "text-ink",
          ].join(" ")}
        >
          {session.groupName}
        </p>
        <p className="mt-0.5 truncate text-[13px] text-ink-2">{ownerSessionSubtitle(session)}</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {attendance ? <Badge tone={attendance.tone}>{attendance.label}</Badge> : null}
          <Badge tone={status.tone}>{status.label}</Badge>
          {duration ? <span className="text-[12px] text-ink-3">{duration}</span> : null}
        </div>
      </div>
    </>
  );

  const shell =
    "flex w-full items-start gap-3 rounded-card border border-line bg-paper p-3.5 text-left shadow-card";

  if (!onSelect) return <div className={shell}>{body}</div>;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`${shell} transition-colors duration-150 active:bg-sunken`}
    >
      {body}
    </button>
  );
}

/** The session's time is over. From then on the owner reads who came first (#ai-feedback 2026-10-26). */
export function sessionEnded(session: Pick<AvailableSession, "endsAt">, now: Date = new Date()): boolean {
  return new Date(session.endsAt).getTime() <= now.getTime();
}

/** The longest wait `setTimeout` takes (about 24.8 days). A later end needs no timer yet. */
const MAX_TIMER_MS = 2_147_483_647;

/**
 * Whether the session ended, and one more render at its end time while the screen is open. The
 * card, the people list and the facts read the clock when they render; without this render an open
 * week keeps the facts of a running session after it ends (review F1.1).
 */
export function useSessionEnded(endsAt: string): boolean {
  const [, setTick] = useState(0);
  useEffect(() => {
    const wait = new Date(endsAt).getTime() - Date.now();
    if (wait <= 0 || wait > MAX_TIMER_MS) return;
    const timer = setTimeout(() => setTick((n) => n + 1), wait + 50);
    return () => clearTimeout(timer);
  }, [endsAt]);
  return sessionEnded({ endsAt });
}

/**
 * Booked ahead: the confirmed bookings without the people added at the session who did not book
 * (`walkInCount`). After the end, Booked shows this number, next to Came.
 */
export function bookedAhead(session: Pick<AvailableSession, "bookedCount" | "walkInCount">): number {
  return Math.max(0, session.bookedCount - (session.walkInCount ?? 0));
}

/** How full the room is, or why this row is not a room at all any more. */
export function ownerSessionBadge(session: AvailableSession, now: Date = new Date()): {
  label: string;
  tone: BadgeTone;
} {
  if (session.status === "cancelled") return { label: "Cancelled", tone: "cancelled" };
  if (session.status === "moved") return { label: "Moved", tone: "quiet" };
  // Places left mean nothing after the end.
  if (sessionEnded(session, now)) return { label: "Ended", tone: "quiet" };
  if (session.spotsLeft === null) return { label: "No limit", tone: "confirmed" };
  return {
    label: `${session.spotsLeft} free`,
    tone: session.spotsLeft === 0 ? "late" : "confirmed",
  };
}

export function ownerSessionSubtitle(session: AvailableSession, now: Date = new Date()): string {
  if (session.status === "moved") return "Moved to a new time";
  if (session.status === "cancelled") return session.cancelReason ?? "Cancelled";
  // After the end, who came leads; Booked is second (older API builds send no count).
  if (sessionEnded(session, now) && session.cameCount !== undefined) return `${session.cameCount} came, ${bookedAhead(session)} booked`;
  // No limit on places: only the count of people booked.
  if (session.capacity === null) return `${session.bookedCount} booked`;
  return `${session.bookedCount} of ${session.capacity} booked`;
}
